import { useMutation } from "@tanstack/react-query"

import supabase from "@/supabase"

interface DVLASubmissionData {
  applicationId: string
  drivingLicenceNumber: string
  licenseFrontUrl?: string
  licenseBackUrl?: string
  attemptNumber?: number // Track number of attempts for license verification
  dvlaConsentAcceptedAt?: string // ISO timestamp of when the driver accepted the DVLA Data Protection Declaration
}

interface RTWSubmissionData {
  applicationId: string
  rtwShareCode: string
  documentUrl?: string
  attemptNumber?: number // Track number of verification attempts
}

interface InsuranceSubmissionData {
  applicationId: string
  insuranceProvider: string
  policyNumber: string
  insuranceProofUrl?: string
}

interface SubmissionResponse {
  success: boolean
  message?: string
  data?: Record<string, unknown>
}

export const useDocumentCloudFunctions = () => {
  // Submit DVLA information to cloud function
  const submitDVLA = useMutation({
    mutationFn: async (data: DVLASubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke("documents-submit-dvla", {
        body: data,
      })

      if (error) {
        console.error("Error submitting DVLA information:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit DVLA information",
          }
        }
      }

      return response
    },
  })

  // Submit Right to Work information to cloud function
  const submitRTW = useMutation({
    mutationFn: async (data: RTWSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke("documents-submit-rtw", {
        body: data,
      })

      if (error) {
        console.error("Error submitting RTW information:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit RTW information",
          }
        }
      }

      return response
    },
  })

  // Submit Insurance information to cloud function
  const submitInsurance = useMutation({
    mutationFn: async (data: InsuranceSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "documents-submit-insurance",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting insurance information:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit insurance information",
          }
        }
      }

      return response
    },
  })

  // --- Additional document submissions ---
  interface CPCSubmissionData {
    applicationId: string
    cpcFrontUrl?: string
    cpcBackUrl?: string
  }

  interface TachoCardSubmissionData {
    applicationId: string
    tachoFrontUrl?: string
    tachoBackUrl?: string
    tachoCardNumber: string
  }

  interface D4MedicalSubmissionData {
    applicationId: string
    d4MedicalReportUrl: string
  }

  const submitCPC = useMutation({
    mutationFn: async (data: CPCSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke("documents-submit-cpc", {
        body: data,
      })

      if (error) {
        console.error("Error submitting CPC certificate:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit CPC certificate",
          }
        }
      }

      return response
    },
  })

  const submitTachoCard = useMutation({
    mutationFn: async (data: TachoCardSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "documents-submit-tacho-card",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting Tacho Card:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit Tacho Card",
          }
        }
      }

      return response
    },
  })

  const submitD4Medical = useMutation({
    mutationFn: async (data: D4MedicalSubmissionData): Promise<SubmissionResponse> => {
      const { data: response, error } = await supabase.functions.invoke(
        "documents-submit-d4-medical",
        {
          body: data,
        },
      )

      if (error) {
        console.error("Error submitting D4 Medical report:", error)
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json()
          return parsedError
        } catch {
          // If parsing fails, fall back to error.message or default message
          return {
            success: false,
            message: error.message || "Failed to submit D4 Medical report",
          }
        }
      }

      return response
    },
  })

  return {
    submitDVLA,
    submitRTW,
    submitInsurance,
    submitCPC,
    submitTachoCard,
    submitD4Medical,
  }
}
